import { JSX, useEffect } from "react";
import { useTranslation } from "react-i18next";
import IconChevron from "@/assets/icons/chevron.svg?react";
import { TaskStatusResponse } from "@/pages/get-don/tasks.types";
import TaskBaseButton from "./TaskBaseButton";
import { cn } from "@/lib/utils";
import Spinner from "@/components/common/Spinner";

export type Status = TaskStatusResponse | "PERFORMED";

const statusVibrationMap: Record<
  Status,
  | "light"
  | "medium"
  | "heavy"
  | "rigid"
  | "soft"
  | "selection"
  | "success"
  | "warning"
> = {
  INCOMPLETE: "soft",
  COMPLETED: "success",
  PENDING: "soft",
  READY_AWARD: "success",
  RETRY_LATER: "warning",
  PERFORMED: "heavy",
};
const TaskButton = ({
  status,
  className,
  onClick,
  showLoading,
  autoClaim,
  disabled,
  customText,
  prefix,
  suffix,
}: {
  status: Status;
  className?: string;
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  showLoading?: boolean;
  vibrate?: boolean;
  autoClaim?: boolean;
  disabled?: boolean;
  customText?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}) => {
  const { t } = useTranslation();

  const buttonStyles: Record<Status, string> = {
    INCOMPLETE: "bg-yellow-linear text-black",
    COMPLETED: "bg-white/08 text-gray-1",
    PENDING: "bg-white/08 text-white",
    READY_AWARD: "bg-green text-white",
    RETRY_LATER: "bg-white/08 text-gray-1",
    PERFORMED: "bg-white text-black",
  };

  useEffect(() => {
    if (autoClaim) {
      onClick?.();
    }
  }, [autoClaim]);

  if (autoClaim) {
    return (
      <TaskBaseButton
        className={cn(
          buttonStyles[status],
          "bg-white/08 text-white",
          className,
          "whitespace-nowrap min-w-[90px]"
        )}
        disabled={disabled}
        vibrationType={statusVibrationMap[status]}
      >
        <Spinner />
      </TaskBaseButton>
    );
  }

  const buttonText = (): JSX.Element => {
    if (showLoading || status === "PENDING") {
      return <Spinner />;
    }

    const text = customText || t(`task_status.${status}`);
    const showChevron = (status === "INCOMPLETE" || status === "PERFORMED") && !suffix;

    return (
      <>
        {prefix}
        {text}
        {suffix}
        {showChevron && (
          <IconChevron
            className="text-black shrink-0"
            width="16"
            height="16"
            strokeWidth="1.6"
          />
        )}
      </>
    );
  };

  const isDisabled =
    showLoading ||
    status === "PENDING" ||
    status === "COMPLETED" ||
    status === "RETRY_LATER" ||
    disabled;

  return (
    <TaskBaseButton
      className={cn(
        buttonStyles[status],
        showLoading && "bg-white/08 text-white",
        className,
        "whitespace-nowrap min-w-[90px]"
      )}
      onClick={onClick}
      disabled={isDisabled}
      vibrationType={statusVibrationMap[status]}
    >
      {buttonText()}
    </TaskBaseButton>
  );
};

export default TaskButton;
